import React, { createContext, useContext, useState, useCallback } from "react";
import { QuoteFormData, WizardStep, ValidationErrors } from "../types/quote";
import { useLocalStorage, clearLocalStorage } from "./useLocalStorage";
import {
  validateEmail,
  validateRequired,
  validateDate,
  validateDestination,
  validatePhone,
} from "../utils/validation";

interface WizardContextType {
  currentStep: WizardStep;
  formData: QuoteFormData;
  errors: ValidationErrors;
  direction: "forward" | "backward";
  updateFormData: (data: Partial<QuoteFormData>) => void;
  setFieldError: (field: string, error: string | undefined) => void;
  validateStep: (step: WizardStep) => boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (step: WizardStep) => void;
  resetWizard: () => void;
  isStepValid: (step: WizardStep) => boolean;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

const initialFormData: QuoteFormData = {
  destination: "",
  freeDescription: "",
  departureAirport: "lisbon",
  otherAirport: "",
  departureDate: "",
  departureDateEnd: "",
  duration: 0,
  flexibleDates: false,
  flexibilityRange: "",
  adults: 2,
  children: 0,
  childAges: [],
  travelPurpose: "summer-holidays",
  packageOptions: ["hotel-accommodation", "flights", "transfers"],
  toursDescription: "",
  boardBasis: "all-inclusive",
  hotelPriorities: [],
  otherHotel: "",
  flightPreferences: [],
  otherFlight: "",
  baggageOptions: [],
  handBaggageCount: 1,
  checkedBaggageCount: 1,
  transferPreferences: [],
  budget: 1500,
  budgetType: "total",
  additionalNotes: "",
  name: "",
  email: "",
  phone: "",
  gdprConsent: false,
};

export const WizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>("welcome");
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const [formData, setFormData] = useLocalStorage<QuoteFormData>("quoteFormData", initialFormData);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const updateFormData = useCallback((data: Partial<QuoteFormData>) => {
    setFormData({ ...formData, ...data });
  }, [formData, setFormData]);

  const setFieldError = useCallback((field: string, error: string | undefined) => {
    setErrors((prev) => {
      if (error) {
        return { ...prev, [field]: error };
      } else {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
    });
  }, []);

  const validateStep1 = useCallback((): boolean => {
    let isValid = true;
    const newErrors: ValidationErrors = {};

    if (!validateDestination(formData.destination)) {
      newErrors.destination = "Please enter a valid destination";
      isValid = false;
    }

    if (!formData.departureAirport) {
      newErrors.departureAirport = "Please select a departure airport";
      isValid = false;
    }

    if (formData.departureAirport === "other" && !validateRequired(formData.otherAirport || "")) {
      newErrors.otherAirport = "Please specify the departure airport";
      isValid = false;
    }

    if (!validateDate(formData.departureDate)) {
      newErrors.departureDate = "Please select a valid departure date";
      isValid = false;
    }

    if (formData.departureDate && !formData.departureDateEnd) {
      newErrors.departureDate = "Please select both start and end dates";
      isValid = false;
    }

    if (formData.departureDate && formData.departureDateEnd) {
      // Parse dates in local time to avoid timezone shifts on iOS Safari
      const parseDateLocal = (dateStr: string): Date => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      };
      const startDate = parseDateLocal(formData.departureDate);
      const endDate = parseDateLocal(formData.departureDateEnd);
      if (endDate <= startDate) {
        newErrors.departureDate = "End date must be after start date";
        isValid = false;
      }
    }

    if (!formData.duration || formData.duration <= 0) {
      newErrors.duration = "Please select a valid date range";
      isValid = false;
    }

    if (formData.flexibleDates && !validateRequired(formData.flexibilityRange || "")) {
      newErrors.flexibilityRange = "Please specify your flexibility range";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  }, [formData]);

  const validateStep2 = useCallback((): boolean => {
    let isValid = true;
    const newErrors: ValidationErrors = {};

    if (!validateRequired(formData.adults) || formData.adults < 1) {
      newErrors.adults = "At least one adult is required";
      isValid = false;
    }

    if (formData.children > 0) {
      const childAges = formData.childAges || [];
      if (childAges.length !== formData.children) {
        newErrors.childAges = "Please enter age for all children";
        isValid = false;
      } else {
        const invalidAges = childAges.some((age) => !age || age <= 0 || age > 17);
        if (invalidAges) {
          newErrors.childAges = "Please enter valid ages (1-17) for all children";
          isValid = false;
        }
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  }, [formData]);

  const validateStep3 = useCallback((): boolean => {
    let isValid = true;
    const newErrors: ValidationErrors = {};

    if (!formData.travelPurpose) {
      newErrors.travelPurpose = "Please select a travel purpose";
      isValid = false;
    }

    if (!formData.packageOptions || formData.packageOptions.length === 0) {
      newErrors.packageOptions = "Please select at least one option";
      isValid = false;
    }

    if (!formData.boardBasis) {
      newErrors.boardBasis = "Please select a board basis";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  }, [formData]);

  const validateStep4 = useCallback((): boolean => {
    let isValid = true;
    const newErrors: ValidationErrors = {};

    const hasHotelOption = formData.packageOptions?.includes("hotel-accommodation") || false;
    const hasFlightOption = formData.packageOptions?.includes("flights") || false;

    // Hotel priorities are only required if hotel/accommodation option is selected
    if (hasHotelOption) {
      if (!formData.hotelPriorities || formData.hotelPriorities.length === 0) {
        newErrors.hotelPriorities = "Please select at least one hotel priority";
        isValid = false;
      } else if (formData.hotelPriorities.length > 2) {
        newErrors.hotelPriorities = "Please select a maximum of 2 hotel priorities";
        isValid = false;
      } else if (formData.hotelPriorities.includes("other") && !formData.otherHotel?.trim()) {
        newErrors.otherHotel = "Please describe what you value most for hotels";
        isValid = false;
      }
    }

    // Flight preferences are only required if flights option is selected
    if (hasFlightOption) {
      if (!formData.flightPreferences || formData.flightPreferences.length === 0) {
        newErrors.flightPreferences = "Please select at least one flight preference";
        isValid = false;
      } else if (formData.flightPreferences.length > 2) {
        newErrors.flightPreferences = "Please select a maximum of 2 flight preferences";
        isValid = false;
      } else if (formData.flightPreferences.includes("other") && !formData.otherFlight?.trim()) {
        newErrors.otherFlight = "Please describe your flight preferences";
        isValid = false;
      }
    }

    // Baggage option is required when flights are selected
    if (hasFlightOption) {
      if (!formData.baggageOptions || formData.baggageOptions.length === 0) {
        newErrors.baggageOptions = "Please select a baggage option";
        isValid = false;
      }
    }

    if (!validateRequired(formData.budget) || formData.budget <= 0) {
      newErrors.budget = "Please enter a valid budget";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  }, [formData]);

  const validateStep5 = useCallback((): boolean => {
    let isValid = true;
    const newErrors: ValidationErrors = {};

    if (!validateRequired(formData.name)) {
      newErrors.name = "Please enter your name";
      isValid = false;
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!formData.gdprConsent) {
      newErrors.gdprConsent = "You must consent to GDPR to continue";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  }, [formData]);

  const validateStep = useCallback(
    (step: WizardStep): boolean => {
      switch (step) {
        case "step1":
          return validateStep1();
        case "step2":
          return validateStep2();
        case "step3":
          return validateStep3();
        case "step4":
          return validateStep4();
        case "step5":
          return validateStep5();
        default:
          return true;
      }
    },
    [validateStep1, validateStep2, validateStep3, validateStep4, validateStep5]
  );

  const isStepValid = useCallback(
    (step: WizardStep): boolean => {
      switch (step) {
        case "step1":
          return (
            validateDestination(formData.destination) &&
            !!formData.departureAirport &&
            (formData.departureAirport !== "other" || validateRequired(formData.otherAirport || "")) &&
            validateDate(formData.departureDate) &&
            !!formData.departureDateEnd &&
            formData.duration > 0 &&
            (!formData.flexibleDates || validateRequired(formData.flexibilityRange || ""))
          );
        case "step2":
          return (
            validateRequired(formData.adults) &&
            formData.adults >= 1 &&
            (formData.children === 0 ||
              (formData.childAges?.length === formData.children &&
                formData.childAges.every((age) => age && age > 0 && age <= 17)))
          );
        case "step3":
          return !!formData.travelPurpose && formData.packageOptions && formData.packageOptions.length > 0 && !!formData.boardBasis;
        case "step4":
          return validateRequired(formData.budget) && formData.budget > 0;
        case "step5":
          return (
            validateRequired(formData.name) &&
            validateEmail(formData.email) &&
            (!formData.phone || validatePhone(formData.phone)) &&
            formData.gdprConsent
          );
        default:
          return true;
      }
    },
    [formData]
  );

  const goToNextStep = useCallback(() => {
    if (currentStep === "welcome") {
      setCurrentStep("step1");
      setDirection("forward");
    } else if (currentStep === "step1") {
      if (validateStep1()) {
        setCurrentStep("step2");
        setDirection("forward");
      }
    } else if (currentStep === "step2") {
      if (validateStep2()) {
        setCurrentStep("step3");
        setDirection("forward");
      }
    } else if (currentStep === "step3") {
      if (validateStep3()) {
        setCurrentStep("step4");
        setDirection("forward");
      }
    } else if (currentStep === "step4") {
      if (validateStep4()) {
        setCurrentStep("step5");
        setDirection("forward");
      }
    }
  }, [currentStep, validateStep1, validateStep2, validateStep3, validateStep4]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep === "step1") {
      setCurrentStep("welcome");
      setDirection("backward");
    } else if (currentStep === "step2") {
      setCurrentStep("step1");
      setDirection("backward");
    } else if (currentStep === "step3") {
      setCurrentStep("step2");
      setDirection("backward");
    } else if (currentStep === "step4") {
      setCurrentStep("step3");
      setDirection("backward");
    } else if (currentStep === "step5") {
      setCurrentStep("step4");
      setDirection("backward");
    }
  }, [currentStep]);

  const goToStep = useCallback((step: WizardStep) => {
    setCurrentStep(step);
  }, []);

  const resetWizard = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep("welcome");
    setDirection("forward");
    setErrors({});
    clearLocalStorage("quoteFormData");
  }, [setFormData]);

  return (
    <WizardContext.Provider
      value={{
        currentStep,
        formData,
        errors,
        direction,
        updateFormData,
        setFieldError,
        validateStep,
        goToNextStep,
        goToPreviousStep,
        goToStep,
        resetWizard,
        isStepValid,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};

export const useWizardState = (): WizardContextType => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error("useWizardState must be used within a WizardProvider");
  }
  return context;
};


